import React, {useState, useEffect} from 'react';
import {i_attach} from "../../icons";
import cfg from "../../constants";
import {getUser} from "../../actions/loginAction";

/**
 * Вложение в переписке.
 *
 * Два способа адресовать файл, и выбор не наш:
 *
 * - у телеграма, Max и VK файл лежит в нашем S3, и адрес складывается из пути и
 *   имени — так было всегда;
 * - у канала DiChat второй копии нет: байты остаются в хранилище Matrix, а
 *   публичная ручка отдаёт их **только по подписанной ссылке**. Подписать её на
 *   фронте нельзя — ключ серверный, — поэтому готовый адрес приходит в поле
 *   `file_url` вместе с сообщением. Строить его самим бесполезно: без подписи
 *   ручка ответит 403.
 *
 * Подпись живёт около шести часов. Куратор держит переписку открытой всю смену,
 * поэтому картинка может не загрузиться на живой странице — и это не «файла
 * нет». На такой случай есть запасной путь: та же ручка под обычной
 * авторизацией панели (`/auth/message/:id/media`), которая отдаёт файл без
 * подписи вовсе. В `<img src>` заголовок Authorization не уходит, поэтому
 * забираем файл запросом и показываем через blob.
 */
export function FilePreview({path, file, url, messageId}) {
  const [error, setError] = useState(false);
  const [isImage, setIsImage] = useState(false);
  const [fallback, setFallback] = useState(null);

  useEffect(() => {
    setIsImage(file ? ['jpg', 'jpeg', 'png', 'webp'].indexOf(file.toLowerCase().split('.').pop()) > -1 : false);
    setError(false);
    setFallback(null);
  }, [file, url]);

  // Blob живёт, пока показывается: без отзыва каждая перерисованная переписка
  // оставляла бы за собой копию файла в памяти вкладки
  useEffect(() => {
    return () => {
      if (fallback) {
        URL.revokeObjectURL(fallback);
      }
    };
  }, [fallback]);

  const src = fallback ? fallback : (url ? absoluteUrl(url) : path + file);

  const handleError = () => {
    // Подписанная ссылка протухла — пробуем авторизованную ручку. Один раз:
    // не вышло и там, значит показываем строку файла, а не крутим запросы
    if (url && messageId && !fallback) {
      loadWithAuth(messageId).then((objectUrl) => {
        if (objectUrl) {
          setFallback(objectUrl);
        } else {
          setError(true);
        }
      });
      return;
    }
    setError(true);
  };

  return <p className="chat__message--file">
    <a href={src} target="_blank" rel="noreferrer" className="attached--icon">
      {!error && isImage && <img src={src} alt={file} onError={handleError} onLoad={() => setError(false)} />}
      {(error || !isImage) && <span>{i_attach()} {file}</span>}
    </a>
  </p>;
}

/**
 * absoluteUrl достраивает адрес, пришедший от API.
 *
 * Приходит он от корня («/auth/file/…»), а панель может жить не на том же
 * хосте, что API, — как на стенде.
 */
function absoluteUrl(url) {
  if (/^https?:\/\//.test(url)) {
    return url;
  }
  return cfg.public_url + url.replace(/^\//, '');
}

/**
 * loadWithAuth забирает вложение авторизованной ручкой и отдаёт адрес blob.
 *
 * Отдаёт null, если не вышло: показывать вместо картинки битый значок честнее,
 * чем пустое место.
 */
function loadWithAuth(messageId) {
  const profile = getUser();
  if (!profile || !profile.token) {
    return Promise.resolve(null);
  }
  return fetch(cfg.base_url + '/auth/message/' + messageId + '/media', {
    headers: {'Authorization': 'Bearer ' + profile.token}
  }).then((res) => {
    if (!res.ok) {
      return null;
    }
    return res.blob().then((blob) => URL.createObjectURL(blob));
  }).catch(() => null);
}
